import { useContext, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { Menu, useMantineTheme } from '@mantine/core';

import { UserContext } from '~/components/providers/UserProvider';
import { CurrencyConvertor } from '~/components/utils/formatters';
import { reportEvent } from '~/domains/analytics';
import { Button } from '~/domains/design-system/Button';
import IconThreeDotsMenu from '~/domains/design-system/icons/IconThreeDotsMenu';
import { useInvitesManagerEnabled } from '~/domains/invites-manager/hooks/useInvitesManagerEnabled';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import { Routes } from '~/domains/common/constants/routes';
import EntryButton from '~/domains/contest/domains/detail/components/EntryButton/EntryButton';
import { getVisibleUserEntriesCount } from '~/domains/contest/domains/common/utils/pickControl';
import { getIsRestrictedInLocation } from '~/domains/contest/domains/common/utils/getIsRestrictedInLocation';

function ContestActionsColumn({ contest, contestPath }: { contest: Contest; contestPath: string }) {
  const { user } = useContext(UserContext);
  const theme = useMantineTheme();
  const router = useRouter();
  const { t } = useTranslation('common');
  const { navigateToInvitesManager } = useInvitesManagerEnabled();

  const handleMenuClick = (callback: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    callback();
  };

  const menuItems = useMemo(
    () => [
      ...(getVisibleUserEntriesCount(contest) > 0
        ? [
            {
              label: t('contestLobby.viewEntries'),
              callback: () => router.push(contestPath),
            },
          ]
        : []),
      {
        label: t('contestLobby.viewContest'),
        callback: () => router.push(Routes.contestDetail(contest.id)),
      },
      ...(user?.id === contest.commissioner.id
        ? [
            {
              label: t('contestLobby.inviteEntrants'),
              callback: () => router.push(Routes.invitesManager(contest.id)),
            },
            {
              label: t('contestLobby.manageEntrants'),
              callback: () => router.push(Routes.entrantsManager(contest.id)),
            },
          ]
        : []),
    ],
    [navigateToInvitesManager, contest, user?.id]
  );

  return getIsRestrictedInLocation(contest) ? null : (
    <Menu position="left" zIndex={99} styles={{ item: { padding: 0 } }}>
      <Menu.Target>
        <Button
          size="small-icon"
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <IconThreeDotsMenu color={theme.colors.gray[6]} />
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item key="entryButton" closeMenuOnClick={false}>
          <EntryButton
            contestId={contest.id}
            entries={contest.entries}
            isAfterDeadline={contest.is_after_first_late_swap_deadline}
            status={contest.status}
            isAllowedInLocation={contest.is_allowed_in_location}
            isEntertainmentOnly={contest.settings?.entertainmentOnlySettings?.enabled}
            isSpectatorMode={contest?.public_viewing}
            onSuccess={(entryIDs) => {
              reportEvent('Lobby > Add Entries (Table)', {
                // Entry fee ($) per one entry
                entry_fee_usd: CurrencyConvertor(contest.entry_fee / 100),
                // Number of multiple entries (1 if only single)
                entries_count: entryIDs.length,
              });
            }}
          >
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              {t('contestLobby.addEntries')}
            </Button>
          </EntryButton>
        </Menu.Item>
        {menuItems.map((menuItem) => (
          <Menu.Item key={menuItem.label} closeMenuOnClick={false}>
            <Button onClick={handleMenuClick(menuItem.callback)} size="small" variant="text">
              {menuItem.label}
            </Button>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

export default ContestActionsColumn;
